import React, { useEffect, useMemo, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { flushSync } from 'react-dom';

import Collapse from '../../Collapse';
import {
  buildHintMessage,
  buildSummary,
  classCleanup,
  deriveGroupMessages,
  ensureErrorVisibility,
  extractDescriptionParagraphs,
  getImagePreview,
  getSeverityKey,
  getSeverityLabel,
  normalizeSnippetText,
  renderRichText,
  scrollToElement,
  severityOrder,
  sortGroupsBySeverity,
} from '../helpers';

import SeverityTag from './SeverityTag';

const ResultsCard = ({ errors }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [openGroupIndex, setOpenGroupIndex] = useState(null);
  const [activeErrorIndex, setActiveErrorIndex] = useState(null);
  const [floatingBarHeight, setFloatingBarHeight] = useState(0);
  const [activeErrorHint, setActiveErrorHint] = useState(null);
  const floatingBarRef = useRef(null);

  useEffect(() => {
    setShowDetails(false);
    setOpenGroupIndex(null);
    setActiveErrorIndex(null);
    setFloatingBarHeight(0);
    setActiveErrorHint(null);
    classCleanup();
  }, [errors]);

  useEffect(() => {
    return () => {
      classCleanup();
    };
  }, []);

  useEffect(() => {
    if (activeErrorIndex === null) {
      setFloatingBarHeight(0);
    }

    if (!floatingBarRef.current || typeof ResizeObserver !== 'function') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      if (!entries?.length) return;
      const [{ contentRect }] = entries;
      window.requestAnimationFrame(() => {
        setFloatingBarHeight(contentRect.height);
      });
    });

    observer.observe(floatingBarRef.current);

    return () => observer.disconnect();
  }, [activeErrorIndex]);

  const computed = useMemo(() => {
    if (!errors || !errors.length) {
      return {
        groups: [],
        summary: {},
        severityChips: [],
        linearErrors: [],
        errorIndexMap: new Map(),
        totalErrors: 0,
      };
    }

    const groups = sortGroupsBySeverity(errors);
    const summary = buildSummary(groups);
    const severityChips = severityOrder.map((impact) => ({
      impact,
      total: summary[impact] ?? 0,
    }));

    const linearErrors = [];
    const errorIndexMap = new Map();
    groups.forEach((group) => {
      (group.errors || []).forEach((error) => {
        errorIndexMap.set(error, linearErrors.length);
        linearErrors.push({ error, group });
      });
    });

    return {
      groups,
      summary,
      severityChips,
      linearErrors,
      errorIndexMap,
      totalErrors: linearErrors.length,
    };
  }, [errors]);

  const { groups, severityChips, linearErrors, errorIndexMap, totalErrors } =
    computed;

  const overallTotal = totalErrors;
  const totalIntro =
    overallTotal === 1 ? 'Foi encontrado' : 'Foram encontradas';
  const totalSuffix =
    overallTotal === 1 ? 'erro de acessibilidade.' : 'erros de acessibilidade.';

  const resetSelection = () => {
    setActiveErrorIndex(null);
    classCleanup();
    setFloatingBarHeight(0);
    setActiveErrorHint(null);
  };

  const handleToggleDetails = () => {
    setShowDetails((prev) => {
      const next = !prev;
      if (!next) {
        resetSelection();
        setOpenGroupIndex(null);
      }
      return next;
    });
  };

  const handleFocusError = (index) => {
    if (index === null || index < 0 || index >= totalErrors) {
      resetSelection();
      return;
    }

    const targetEntry = linearErrors[index];
    const targetError = targetEntry?.error;
    const groupIndex = groups.indexOf(targetEntry?.group);

    flushSync(() => {
      if (!showDetails) {
        setShowDetails(true);
      }
      if (groupIndex >= 0 && openGroupIndex !== groupIndex) {
        setOpenGroupIndex(groupIndex);
      }
      setActiveErrorIndex(index);
    });

    setActiveErrorHint(null);

    const applyHighlight = (showHint = true) => {
      if (!targetError?.el) {
        classCleanup();
        if (showHint) {
          setActiveErrorHint({
            message:
              'Não foi possível localizar o elemento correspondente na página. Verifique se o conteúdo está disponível.',
          });
        }
        return;
      }

      const visibility = ensureErrorVisibility(targetError.el);
      const highlightTarget = visibility.highlightElement;
      const highlightClass = visibility.usedFallback
        ? 'a11y-error-container'
        : 'a11y-error';

      if (highlightTarget) {
        scrollToElement(highlightTarget, { highlightClass });
      } else {
        classCleanup();
      }

      if (showHint) {
        setActiveErrorHint(buildHintMessage(visibility) || null);
      }
    };

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        window.setTimeout(applyHighlight, 120);
      });
    } else {
      applyHighlight();
    }
  };

  const navigateTo = (direction) => {
    if (!totalErrors) return;
    let nextIndex;
    if (activeErrorIndex === null) {
      nextIndex = direction === 'prev' ? totalErrors - 1 : 0;
    } else if (direction === 'next') {
      nextIndex = (activeErrorIndex + 1) % totalErrors;
    } else {
      nextIndex = (activeErrorIndex - 1 + totalErrors) % totalErrors;
    }
    handleFocusError(nextIndex);
  };

  const groupsContainerId = 'a11y-results-groups';
  const activeErrorEntry =
    activeErrorIndex !== null ? linearErrors[activeErrorIndex] : null;
  const activeGroup = activeErrorEntry?.group;
  const activeError = activeErrorEntry?.error;
  const activeImpactKey = getSeverityKey(activeError?.impact);
  const activeImpactClass = `impact-${activeImpactKey}`;
  const hasActiveError = activeErrorIndex !== null;

  const navigationControls = (
    <div
      className="a11y-nav-group"
      role="group"
      aria-label="Navegar pelos erros"
    >
      <button
        type="button"
        className="br-button circle small"
        onClick={() => navigateTo('prev')}
        disabled={!totalErrors}
        title="Ir para erro anterior"
        aria-label="Ir para erro anterior"
      >
        <span className="fa-solid fa-chevron-left" />
      </button>
      <span className="a11y-nav-index" aria-live="polite">
        {hasActiveError ? activeErrorIndex + 1 : 0}/{totalErrors}
      </span>
      <button
        type="button"
        className="br-button circle small"
        onClick={() => navigateTo('next')}
        disabled={!totalErrors}
        title="Ir para próximo erro"
        aria-label="Ir para próximo erro"
      >
        <span className="fa-solid fa-chevron-right" />
      </button>
    </div>
  );

  const hintClassNames = ['a11y-current-error-hint'];
  if (activeErrorHint?.type === 'fallback') {
    hintClassNames.push('is-fallback');
  }

  const handleRetryHighlight = () => {
    if (activeErrorIndex === null) return;
    const currentEntry = linearErrors[activeErrorIndex];
    const currentError = currentEntry?.error;
    if (!currentError?.el) {
      handleFocusError(activeErrorIndex);
      return;
    }

    const visibility = ensureErrorVisibility(currentError.el);
    const target = visibility.highlightElement;
    const highlightClass = visibility.usedFallback
      ? 'a11y-error-container'
      : 'a11y-error';

    if (target) {
      scrollToElement(target, { highlightClass });
    } else {
      classCleanup();
    }

    setActiveErrorHint(buildHintMessage(visibility) || null);
  };

  if (!errors || !errors.length) {
    return null;
  }

  return (
    <section
      className={`a11y-panel a11y-panel--danger a11y-results-card${
        showDetails ? ' is-open' : ''
      }`}
      aria-labelledby="a11y-results-title"
    >
      <div className="a11y-panel__content">
        <div className="a11y-results-header">
          <div className="a11y-results-title" id="a11y-results-title">
            <span className="a11y-results-intro">{totalIntro}</span>
            <span className="a11y-results-total">{overallTotal}</span>
            <span className="a11y-results-intro">{totalSuffix}</span>
          </div>
          <div className="a11y-header-controls">
            <div
              className="a11y-summary-badges"
              role="group"
              aria-label="Resumo por severidade"
            >
              {severityChips.flatMap(({ impact, total }) => {
                if (!total) {
                  return [];
                }
                const severityLabel = getSeverityLabel(impact);
                return [
                  <SeverityTag
                    key={impact}
                    impact={impact}
                    count={total}
                    className="a11y-severity-tag"
                    title={`${severityLabel} (${total})`}
                    aria-label={`${severityLabel}: ${total}`}
                  />,
                ];
              })}
            </div>
            <button
              type="button"
              className="br-button tertiary circle"
              onClick={handleToggleDetails}
              aria-expanded={showDetails}
              aria-controls={groupsContainerId}
              aria-label={
                showDetails
                  ? 'Ocultar lista de erros'
                  : 'Mostrar lista de erros'
              }
            >
              <span
                className={`a11y-toggle-icon fa-solid fa-chevron-${
                  showDetails ? 'up' : 'down'
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <AnimateHeight
          duration={240}
          height={showDetails ? 'auto' : 0}
          animateOpacity
        >
          <div className="a11y-groups" role="list" id={groupsContainerId}>
            {groups.map((group, index) => {
              const { sharedIntro, sharedDetails, items } = deriveGroupMessages(
                group.errors || [],
              );
              const impactKey = getSeverityKey(group.impact);
              const total = group.errors ? group.errors.length : 0;
              const impactClass = `impact-${impactKey}`;
              const isGroupOpen = openGroupIndex === index;
              const impactLabel = getSeverityLabel(impactKey);
              const ruleTitle = group.help || group.title || group.ruleId;
              const descriptionParagraphs = extractDescriptionParagraphs(
                group.description,
              );
              const [descriptionLead, ...descriptionRemaining] =
                descriptionParagraphs;
              const descriptionBody = descriptionRemaining.length
                ? descriptionRemaining.join(' ')
                : null;
              const hasDescription = Boolean(
                descriptionLead || descriptionBody,
              );
              const hasSharedSummary = Boolean(
                sharedIntro || (sharedDetails && sharedDetails.length),
              );
              const sharedSummaryId = hasSharedSummary
                ? `a11y-shared-summary-${index}`
                : null;

              return (
                <div
                  key={`${ruleTitle || 'grupo'}-${index}`}
                  className="a11y-group-wrapper"
                >
                  <Collapse
                    title={
                      <span className="a11y-collapse-title">
                        <span className={`a11y-badge ${impactClass}`}>
                          {impactLabel}
                        </span>
                        <span className="a11y-group-title-text">
                          {ruleTitle}
                        </span>
                        <span
                          className={`a11y-count-badge ${impactClass}`}
                          title="Total de ocorrências"
                          aria-label={`Total de ocorrências: ${total}`}
                        >
                          {total}
                        </span>
                      </span>
                    }
                    className="a11y-collapse"
                    isOpen={isGroupOpen}
                    onToggle={(nextOpen) => {
                      setOpenGroupIndex(nextOpen ? index : null);
                      if (nextOpen && !showDetails) {
                        setShowDetails(true);
                      }
                    }}
                  >
                    {hasDescription ? (
                      <div
                        className={`a11y-group-description ${impactClass}`}
                        role="note"
                      >
                        {descriptionLead ? (
                          <p className="a11y-group-description__lead">
                            {renderRichText(descriptionLead)}
                          </p>
                        ) : null}
                        {descriptionBody ? (
                          <p className="a11y-group-description__body">
                            {renderRichText(descriptionBody)}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {hasSharedSummary ? (
                      <div
                        className={`a11y-group-shared ${impactClass}`}
                        role="note"
                        id={sharedSummaryId}
                      >
                        {sharedIntro ? (
                          <p className="a11y-group-shared__intro">
                            {renderRichText(sharedIntro)}
                          </p>
                        ) : null}
                        {sharedDetails.length ? (
                          <ul className="a11y-group-shared__list">
                            {sharedDetails.map((detail, detailIndex) => (
                              <li key={`shared-detail-${index}-${detailIndex}`}>
                                {renderRichText(detail)}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="br-list" id={`a-${index}`} role="list">
                      {items.map(
                        (
                          {
                            error: err,
                            summaryIntro,
                            summaryDetails,
                            rawSummary,
                          },
                          itemIndex,
                        ) => {
                          const globalIndex = errorIndexMap.get(err);
                          const isActive =
                            typeof globalIndex === 'number' &&
                            activeErrorIndex === globalIndex;
                          const hasErrorIndex = typeof globalIndex === 'number';
                          const fallbackSummary = [
                            summaryIntro,
                            ...(summaryDetails || []),
                          ]
                            .filter(Boolean)
                            .join(' ');
                          const snippetLabel =
                            err.displayText ||
                            fallbackSummary ||
                            rawSummary ||
                            err?.msg ||
                            '';
                          const normalizedSnippet =
                            normalizeSnippetText(snippetLabel) ||
                            'Detalhes indisponíveis';
                          const displayTextSnippet = normalizeSnippetText(
                            err.displayText || '',
                          );
                          const snippetTextClasses = [
                            'a11y-error-snippet__text',
                          ];
                          if (displayTextSnippet) {
                            snippetTextClasses.push('a11y-quoted');
                          }
                          const impactKeyItem = getSeverityKey(err?.impact);
                          const itemClasses = [
                            'br-item',
                            'error-item',
                            `impact-${impactKeyItem}`,
                            isActive ? 'is-active' : null,
                          ]
                            .filter(Boolean)
                            .join(' ');
                          const imagePreview = getImagePreview(err?.el);
                          const hasItemSummary = Boolean(
                            (summaryIntro && summaryIntro.length) ||
                              (summaryDetails && summaryDetails.length),
                          );
                          const itemSummaryId = hasItemSummary
                            ? `a11y-error-summary-${index}-${itemIndex}`
                            : null;
                          const describedByIds = [
                            sharedSummaryId,
                            itemSummaryId,
                          ]
                            .filter(Boolean)
                            .join(' ');

                          const handleItemActivation = () => {
                            handleFocusError(
                              hasErrorIndex ? globalIndex : null,
                            );
                          };

                          return (
                            <div
                              key={`error-${index}-${itemIndex}`}
                              className={itemClasses}
                              role="listitem"
                              aria-current={isActive ? 'true' : undefined}
                            >
                              <button
                                type="button"
                                className="a11y-error-trigger"
                                onClick={handleItemActivation}
                                title="Exibir o erro"
                                aria-describedby={describedByIds || undefined}
                              >
                                <span
                                  className="a11y-error-bullet"
                                  aria-hidden="true"
                                />
                                <span className="a11y-error-content">
                                  {imagePreview ? (
                                    <span className="a11y-error-thumb">
                                      <img
                                        src={imagePreview.src}
                                        alt={imagePreview.alt}
                                        loading="lazy"
                                      />
                                    </span>
                                  ) : null}
                                  <span className="a11y-error-text">
                                    {normalizedSnippet ? (
                                      <span
                                        className="a11y-error-snippet"
                                        title={normalizedSnippet}
                                      >
                                        <span
                                          className={snippetTextClasses.join(
                                            ' ',
                                          )}
                                        >
                                          {normalizedSnippet}
                                        </span>
                                      </span>
                                    ) : null}
                                    {hasItemSummary ? (
                                      <span
                                        className="a11y-error-summary"
                                        id={itemSummaryId || undefined}
                                      >
                                        {(() => {
                                          if (!summaryIntro) return null;
                                          const normalizedIntro =
                                            normalizeSnippetText(
                                              typeof summaryIntro === 'string'
                                                ? summaryIntro
                                                : '',
                                            );
                                          const hideDefaultIntro =
                                            normalizedIntro?.toLowerCase?.() ===
                                              'corrija qualquer um dos itens a seguir:' &&
                                            (summaryDetails?.length || 0) <= 1;
                                          if (hideDefaultIntro) {
                                            return null;
                                          }
                                          return (
                                            <span className="a11y-error-summary__intro">
                                              {renderRichText(summaryIntro)}
                                            </span>
                                          );
                                        })()}
                                        {(summaryDetails || []).map(
                                          (detail, detailIndex) => (
                                            <span
                                              key={`error-detail-${index}-${itemIndex}-${detailIndex}`}
                                              className="a11y-error-summary__detail"
                                            >
                                              {renderRichText(detail)}
                                            </span>
                                          ),
                                        )}
                                      </span>
                                    ) : null}
                                  </span>
                                </span>
                              </button>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </Collapse>
                  {index < groups.length - 1 ? (
                    <hr className="a11y-group-separator" />
                  ) : null}
                </div>
              );
            })}
          </div>
        </AnimateHeight>
        {hasActiveError ? (
          <div
            className="a11y-floating-spacer"
            aria-hidden="true"
            style={{ height: floatingBarHeight || 0 }}
          />
        ) : null}
        {activeError ? (
          <div
            className="a11y-floating-bar"
            role="region"
            aria-live="polite"
            ref={floatingBarRef}
          >
            <div className="a11y-floating-bar-inner">
              <div
                className={`a11y-current-error ${activeImpactClass}`}
                role="status"
              >
                <div className="a11y-current-error-meta">
                  <div className="a11y-current-error-overview">
                    <SeverityTag
                      impact={activeImpactKey}
                      className="a11y-current-error-severity"
                      aria-label={`Severidade: ${getSeverityLabel(activeImpactKey)}`}
                      title={getSeverityLabel(activeImpactKey)}
                    />
                    <span className="a11y-current-error-title">
                      {activeGroup?.title || 'Erro selecionado'}
                    </span>
                  </div>
                  <div className="a11y-current-error-actions">
                    {navigationControls}
                    <button
                      type="button"
                      className="br-button circle small"
                      onClick={resetSelection}
                      title="Fechar painel de correções"
                      aria-label="Fechar painel de correções"
                    >
                      <span className="fa-solid fa-xmark" />
                    </button>
                  </div>
                </div>
                {activeErrorHint ? (
                  <div className={hintClassNames.join(' ')} role="status">
                    <span
                      className="fa-solid fa-circle-info"
                      aria-hidden="true"
                    />
                    <div className="a11y-current-error-hint__body">
                      <span className="a11y-current-error-hint__text">
                        {activeErrorHint.message}
                      </span>
                      {activeErrorHint.instruction ? (
                        <p className="a11y-current-error-hint__instruction">
                          {activeErrorHint.instruction}
                        </p>
                      ) : null}
                    </div>
                    {activeErrorHint.canRetry ? (
                      <button
                        type="button"
                        className="br-button tertiary small"
                        onClick={handleRetryHighlight}
                      >
                        Atualizar
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="a11y-current-error-message">
                  {renderRichText(activeError?.msg || '')}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ResultsCard;
